import axios, { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import { ClassConstructor, plainToClass } from 'class-transformer';
import { RequestConfig } from '@/interfaces/request-config.interface';
import SessionService from '@/services/session.service';
import { IKeyValue } from '@/interfaces/key-value.interface';
import { cloneDeep } from 'lodash';

export default class HttpService {
  private axios: AxiosInstance;
  private interceptorId: number | null = null;

  constructor() {
    this.axios = axios.create();
    this.configureTokenInterceptor(SessionService.getToken());
    this.configure401Interceptor();
  }

  withoutToken(): HttpService {
    const clone = cloneDeep(this);
    clone.axios = axios.create();
    return clone;
  }

  public reconfigureAxios(): void {
    this.configureTokenInterceptor(SessionService.getToken());
  }

  async head<T>(
    cls: ClassConstructor<T>,
    url: string,
    config?: RequestConfig,
    formatError = true
  ): Promise<T> {
    const response = await HttpService.runRequest<T>(this.axios.head(url, config), formatError);
    return plainToClass(cls, response);
  }

  async options<T>(
    cls: ClassConstructor<T>,
    url: string,
    config?: RequestConfig,
    formatError = true
  ): Promise<T> {
    const response = await HttpService.runRequest<T>(
      this.axios.options(url, config),
      formatError
    );
    return plainToClass(cls, response);
  }

  async get<T>(
    cls: ClassConstructor<T>,
    url: string,
    config?: RequestConfig,
    formatError = true
  ): Promise<T | T[]> {
    const response = await HttpService.runRequest<T>(this.axios.get(url, config), formatError);
    return plainToClass(cls, response);
  }

  async post<T>(
    cls: ClassConstructor<T>,
    url: string,
    data?: unknown,
    config?: RequestConfig,
    formatError = true
  ): Promise<T | T[]> {
    const response = await HttpService.runRequest<T>(
      this.axios.post(url, data, config),
      formatError
    );
    return plainToClass(cls, response);
  }

  async put<T>(
    cls: ClassConstructor<T>,
    url: string,
    data?: unknown,
    config?: RequestConfig,
    formatError = true
  ): Promise<T | T[]> {
    const response = await HttpService.runRequest<T>(
      this.axios.put(url, data, config),
      formatError
    );
    return plainToClass(cls, response);
  }

  async patch<T>(
    cls: ClassConstructor<T>,
    url: string,
    data?: unknown,
    config?: RequestConfig,
    formatError = true
  ): Promise<T | T[]> {
    const response = await HttpService.runRequest<T>(
      this.axios.patch(url, data, config),
      formatError
    );
    return plainToClass(cls, response);
  }

  delete(url: string, config?: RequestConfig, formatError = true): Promise<void> {
    return HttpService.runRequest<void>(this.axios.delete(url, config), formatError);
  }

  getAuthorizationHeader(): IKeyValue | null {
    return this.axios.defaults.headers.Authorization as IKeyValue || null;
  }

  private configureTokenInterceptor(token: string): AxiosInstance {
    if (this.interceptorId !== null) {
      this.axios.interceptors.request.eject(this.interceptorId);
    }
    this.interceptorId = this.axios.interceptors.request.use((config) => {
      const cfg = { ...config };
      if (!cfg.headers.Authorization && token) {
        cfg.headers.Authorization = `Bearer ${token}`;
      }
      return cfg;
    });

    return this.axios;
  }

  private configure401Interceptor(): void {
    this.axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const isAuthEndpoint = error.config?.url?.includes('/auth/authenticate');
        if (error.response?.status === 401 && !isAuthEndpoint) {
          SessionService.destroySession();
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  static async runRequest<T>(
    request: Promise<AxiosResponse<T>>,
    formatError = true
  ): Promise<T> {
    try {
      const response = await request;
      return response.data;
    } catch (error) {
      if (formatError) {
        HttpService.formatError(error as AxiosError);
      }
      throw error;
    }
  }

  private static formatError(error: AxiosError): void {
    if (error.response) {
      // Server responded with error status
      const responseData = error.response.data as any;
      const message =
        (typeof responseData === 'string' && responseData) ||
        responseData?.message ||
        responseData?.error ||
        responseData?.detail ||
        error.message;
      throw new Error(message);
    } else if (error.request) {
      // Request made but no response
      throw new Error('Servidor não disponível. Por favor, tente novamente.');
    } else {
      // Something else happened
      throw new Error(error.message);
    }
  }
}
